// Data display — board section 04: the surfaces and read-only primitives that
// present records — Card, Avatar, StatCard, Alert, and the DataTable stack
// (toolbar · table · footer) shown as one composed panel.

import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  AvatarStack,
  Button,
  Card,
  CardsLoadMore,
  Chip,
  ColumnsMenu,
  DataBoard,
  DataCards,
  DataTable,
  Icon,
  IconButton,
  SegmentedControl,
  StatCard,
  TableFooter,
  TableToolbar,
  type AvatarTone,
  type DataBoardColumn,
  type DataTableColumn,
} from '@/components/common';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import Spec from './Spec';

const AVATAR_TONES: AvatarTone[] = ['brand', 'blue', 'purple', 'green', 'amber', 'red'];

// Inline so the image-avatar demo renders with no network.
const SAMPLE_PHOTO =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><rect width='64' height='64' fill='%230E7A3B'/><circle cx='32' cy='24' r='11' fill='%23ffffff' opacity='.9'/><ellipse cx='32' cy='56' rx='20' ry='16' fill='%23ffffff' opacity='.9'/></svg>";

interface DemoSite {
  id: string;
  site: string;
  crew: string;
  status: 'complete' | 'progress' | 'overdue';
  due: string;
  findings: number;
}

const DEMO_ROWS: DemoSite[] = [
  {
    id: '1',
    site: 'Warehouse North',
    crew: 'Crew A',
    status: 'complete',
    due: '12 Aug 2026',
    findings: 0,
  },
  {
    id: '2',
    site: 'Loading Bay 2',
    crew: 'Crew B',
    status: 'progress',
    due: '19 Aug 2026',
    findings: 3,
  },
  {
    id: '3',
    site: 'Rooftop Units',
    crew: 'Crew C',
    status: 'overdue',
    due: '02 Aug 2026',
    findings: 7,
  },
  {
    id: '4',
    site: 'Cold Store',
    crew: 'Crew A',
    status: 'progress',
    due: '28 Aug 2026',
    findings: 1,
  },
];

const STATUS_CHIP = {
  complete: { variant: 'success', label: 'Complete' },
  progress: { variant: 'warning', label: 'In progress' },
  overdue: { variant: 'danger', label: 'Overdue' },
} as const;

const TABLE_STATES = [
  { value: 'data', label: 'Data' },
  { value: 'loading', label: 'Loading' },
  { value: 'error', label: 'Error' },
  { value: 'empty', label: 'Empty' },
];

/** The full table stack — toolbar, columns menu, sorting, selection, footer,
 *  and a switcher for the three non-data states. */
const DataTableDemo = () => {
  const [state, setState] = useState('data');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(['2']));
  const [sort, setSort] = useState<{ key: string; type: 'asc' | 'desc' }>({
    key: 'site',
    type: 'asc',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);

  const columns = useMemo<DataTableColumn<DemoSite>[]>(
    () => [
      { key: 'site', label: 'Site', sortable: true, render: (r) => r.site },
      {
        key: 'crew',
        label: 'Crew',
        render: (r) => (
          <span className='theme-page__cell-identity'>
            <Avatar initials={r.crew.slice(-1)} size={28} tone='blue' />
            {r.crew}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (r) => (
          <Chip variant={STATUS_CHIP[r.status].variant} dot>
            {STATUS_CHIP[r.status].label}
          </Chip>
        ),
      },
      { key: 'due', label: 'Due', sortable: true, render: (r) => r.due },
      {
        key: 'findings',
        label: 'Findings',
        align: 'end',
        sortable: true,
        render: (r) => r.findings,
      },
      {
        key: 'actions',
        label: '',
        align: 'end',
        render: () => (
          <IconButton icon='ellipsis-vertical' variant='ghost' size='sm' aria-label='Row actions' />
        ),
      },
    ],
    [],
  );

  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    locked: ['site'],
    excluded: ['actions'],
  });

  const rows = state === 'data' ? DEMO_ROWS : [];
  const selectableIds = rows.map((r) => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className='theme-page__col'>
      <SegmentedControl
        options={TABLE_STATES}
        value={state}
        onChange={setState}
        aria-label='Table state'
      />
      <Card padded={false}>
        <TableToolbar count={rows.length} countLabel='sites'>
          <ColumnsMenu {...menuProps} />
        </TableToolbar>
        <div className='theme-page__table-scroll'>
          <DataTable
            columns={visibleColumns}
            rows={rows}
            rowKey={(r) => r.id}
            minWidth={720}
            selectable
            selectedIds={selected}
            onToggleSelect={toggle}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={() => setSelected(allSelected ? new Set() : new Set(selectableIds))}
            selectAllLabel='Select all sites'
            isRowLocked={(r) => r.id === '1'}
            isRowMuted={(r) => r.status === 'overdue'}
            sort={sort}
            onSort={(key) =>
              setSort((prev) => ({
                key,
                type: prev.key === key && prev.type === 'asc' ? 'desc' : 'asc',
              }))
            }
            onRowClick={() => {}}
            loading={state === 'loading'}
            error={state === 'error'}
            loadingText='Loading sites…'
            errorText='Could not load sites.'
            empty={
              <>
                <Icon name='inbox' size={22} />
                No sites match this filter.
              </>
            }
          />
        </div>
        <TableFooter
          page={page}
          limit={limit}
          total={rows.length}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel='Items per page'
          rangeText={`1–${rows.length} of ${rows.length} sites`}
        />
      </Card>
    </div>
  );
};

interface DemoCardRecord {
  id: string;
  title: string;
  status: DemoSite['status'];
  note: string;
  owner: string;
}

// Deliberately generic — the shells are the component's, the innards are each
// module's, so the demo only has to exercise the two render slots and states.
const DEMO_CARDS: DemoCardRecord[] = [
  { id: 'c1', title: 'Warehouse North', status: 'complete', note: 'Signed off.', owner: 'Omar K.' },
  {
    id: 'c2',
    title: 'Loading Bay 2',
    status: 'progress',
    note: '3 findings open.',
    owner: 'Maya S.',
  },
  { id: 'c3', title: 'Rooftop Units', status: 'overdue', note: 'Due 2 Aug.', owner: 'Sara N.' },
  { id: 'c4', title: 'Cold Store', status: 'progress', note: '1 finding open.', owner: 'Dana F.' },
  { id: 'c5', title: 'Dock Office', status: 'complete', note: 'Signed off.', owner: 'Omar K.' },
  { id: 'c6', title: 'Boiler Room', status: 'overdue', note: 'Due 9 Aug.', owner: 'Maya S.' },
  { id: 'c7', title: 'Yard Gate', status: 'progress', note: 'Crew assigned.', owner: 'Sara N.' },
  { id: 'c8', title: 'Mezzanine', status: 'complete', note: 'Signed off.', owner: 'Dana F.' },
];

const PAGE_SIZE = 4;

/** The card-grid stack — DataCards (shell/grid/states) + CardsLoadMore, with a
 *  switcher for the three non-data states. Card innards are demo-local, as in
 *  product modules. */
const DataCardsDemo = () => {
  const [state, setState] = useState('data');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);

  const items = state === 'data' ? DEMO_CARDS.slice(0, visible) : [];

  const loadMore = () => {
    setLoadingMore(true);
    // Fake a page fetch so the button's spinner state is visible.
    setTimeout(() => {
      setVisible((v) => v + PAGE_SIZE);
      setLoadingMore(false);
    }, 600);
  };

  return (
    <div className='theme-page__col'>
      <SegmentedControl
        options={TABLE_STATES}
        value={state}
        onChange={(next) => {
          setState(next);
          setVisible(PAGE_SIZE);
        }}
        aria-label='Cards state'
      />
      <div>
        <DataCards
          items={items}
          itemKey={(i) => i.id}
          onItemClick={() => {}}
          loading={state === 'loading'}
          error={state === 'error'}
          loadingText='Loading sites…'
          errorText='Could not load sites.'
          empty={
            <>
              <Icon name='inbox' size={22} />
              No sites match this filter.
            </>
          }
          renderCard={(i) => (
            <div className='theme-page__col'>
              <b>{i.title}</b>
              <span className='theme-page__cell-identity'>
                <Chip variant={STATUS_CHIP[i.status].variant} dot>
                  {STATUS_CHIP[i.status].label}
                </Chip>
              </span>
              <span>{i.note}</span>
            </div>
          )}
          renderFooter={(i) => (
            <span className='theme-page__cell-identity'>
              <Avatar initials={i.owner.slice(0, 1)} size={28} tone='green' />
              {i.owner}
            </span>
          )}
        />
        <CardsLoadMore
          hasMore={state === 'data' && visible < DEMO_CARDS.length}
          loading={loadingMore}
          onLoadMore={loadMore}
          label='Load more'
        />
      </div>
    </div>
  );
};

// Columns reuse the card fixtures' status values; 'paused' stays empty so the
// per-column empty label is visible.
const BOARD_COLUMNS: DataBoardColumn[] = [
  { key: 'progress', label: 'In progress', tone: 'warning' },
  { key: 'complete', label: 'Complete', tone: 'success' },
  { key: 'overdue', label: 'Overdue', tone: 'danger' },
  { key: 'paused', label: 'Paused' },
];

/** The Kanban stack — DataBoard owns the track, columns, headers and states;
 *  card innards are demo-local via renderCard, as in product modules. */
const DataBoardDemo = () => {
  const [state, setState] = useState('data');

  return (
    <div className='theme-page__col'>
      <SegmentedControl
        options={TABLE_STATES}
        value={state}
        onChange={setState}
        aria-label='Board state'
      />
      <DataBoard
        columns={BOARD_COLUMNS}
        items={state === 'data' ? DEMO_CARDS : []}
        itemKey={(i) => i.id}
        columnKey={(i) => i.status}
        onItemClick={() => {}}
        columnWidth={260}
        emptyColumnText='No sites'
        loading={state === 'loading'}
        error={state === 'error'}
        loadingText='Loading sites…'
        errorText='Could not load sites.'
        empty={
          <>
            <Icon name='inbox' size={22} />
            No sites match this filter.
          </>
        }
        renderCard={(i) => (
          <div className='theme-page__col'>
            <b>{i.title}</b>
            <span>{i.note}</span>
            <span className='theme-page__cell-identity'>
              <Avatar initials={i.owner.slice(0, 1)} size={28} tone='green' />
              {i.owner}
            </span>
          </div>
        )}
      />
    </div>
  );
};

const DataDisplaySection = () => (
  <section className='theme-page__section'>
    <h2 className='theme-page__section-title'>
      <Icon name='layout-grid' size={18} /> 04 · Data display
    </h2>

    {/* ---- Card ---- */}
    <h3 className='theme-page__block-title'>
      Card
      <span className='theme-page__note'>
        `head` splits a bordered header off the body; both self-pad.
      </span>
    </h3>
    <div className='theme-page__row theme-page__row--top'>
      <Card style={{ width: 240 }}>
        <p className='theme-page__card-title'>padded (default)</p>
        <span className='theme-page__token-value'>surface · border · sh-sm</span>
      </Card>
      <Card padded={false} style={{ width: 240 }}>
        <p className='theme-page__card-title' style={{ padding: '1rem', margin: 0 }}>
          padded={'{false}'}
        </p>
      </Card>
      <Card
        style={{ width: 260 }}
        head={
          <div className='theme-page__row' style={{ justifyContent: 'space-between' }}>
            <span className='theme-page__card-title' style={{ margin: 0 }}>
              With head
            </span>
            <Button variant='ghost' size='sm' iconStart='ellipsis'>
              Actions
            </Button>
          </div>
        }
      >
        <span className='theme-page__token-value'>head + padded body</span>
      </Card>
    </div>

    {/* ---- Avatar ---- */}
    <h3 className='theme-page__block-title'>Avatar · tone</h3>
    <div className='theme-page__variants'>
      {AVATAR_TONES.map((tone) => (
        <Spec key={tone} label={`tone='${tone}'`}>
          <Avatar initials={tone.slice(0, 2).toUpperCase()} tone={tone} />
        </Spec>
      ))}
      <Spec label='src (photo wins over tone)'>
        <Avatar initials='OK' src={SAMPLE_PHOTO} alt='Sample user' />
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>
      Avatar · status &amp; size
      <span className='theme-page__note'>`size` is a raw pixel number, not a named scale.</span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label="status='online'">
        <Avatar initials='AH' status='online' />
      </Spec>
      <Spec label="status='busy'">
        <Avatar initials='BN' status='busy' />
      </Spec>
      <Spec label="status='away'">
        <Avatar initials='CE' status='away' />
      </Spec>
      <Spec label="status='offline'">
        <Avatar initials='DF' status='offline' />
      </Spec>
      <Spec label='size={28}'>
        <Avatar initials='OK' size={28} />
      </Spec>
      <Spec label='size={34} (default)'>
        <Avatar initials='MS' size={34} />
      </Spec>
      <Spec label='size={44}'>
        <Avatar initials='RD' size={44} />
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>AvatarStack</h3>
    <div className='theme-page__variants'>
      <Spec label='no max — all shown'>
        <AvatarStack>
          <Avatar initials='OK' tone='brand' />
          <Avatar initials='MS' tone='blue' />
          <Avatar initials='RD' tone='purple' />
        </AvatarStack>
      </Spec>
      <Spec label='max={3} of 5'>
        <AvatarStack max={3}>
          <Avatar initials='OK' tone='brand' />
          <Avatar initials='MS' tone='blue' />
          <Avatar initials='RD' tone='purple' />
          <Avatar initials='AH' tone='green' />
          <Avatar initials='BN' tone='amber' />
        </AvatarStack>
      </Spec>
      <Spec label='max={2} size={28}'>
        <AvatarStack max={2} size={28}>
          <Avatar initials='OK' size={28} tone='red' />
          <Avatar initials='MS' size={28} tone='amber' />
          <Avatar initials='RD' size={28} tone='green' />
        </AvatarStack>
      </Spec>
    </div>

    {/* ---- StatCard ---- */}
    <h3 className='theme-page__block-title'>
      StatCard · tone &amp; delta
      <span className='theme-page__note'>
        Values arrive pre-formatted — the tile never does maths.
      </span>
    </h3>
    <div className='theme-page__grid theme-page__grid--stats'>
      <StatCard icon='clipboard-check' tone='brand' value='128' label='Inspections' />
      <StatCard
        icon='circle-check'
        tone='success'
        value='92'
        unit='%'
        label='Pass rate'
        delta={{ text: '+4%', icon: 'trending-up', tone: 'success' }}
      />
      <StatCard
        icon='triangle-alert'
        tone='warning'
        value='7'
        unit=' open'
        label='Findings'
        delta={{ text: '-2', icon: 'trending-down', tone: 'danger' }}
      />
      <StatCard icon='users' tone='info' value='34' label='Active crew' />
      <StatCard
        icon='clock'
        tone='neutral'
        value='—'
        label='Avg. duration'
        delta={{ text: '0', icon: 'minus', tone: 'neutral' }}
      />
    </div>

    {/* ---- Alert ---- */}
    <h3 className='theme-page__block-title'>
      Alert · variant
      <span className='theme-page__note'>
        Each variant carries a default icon; `icon` overrides it.
      </span>
    </h3>
    <div className='theme-page__col'>
      <Alert variant='success' title='Submission approved'>
        The floor inspection was signed off by the supervisor.
      </Alert>
      <Alert variant='info' title='Sync in progress'>
        3 forms are queued and will upload when back online.
      </Alert>
      <Alert variant='warning' title='Missing signature'>
        This report needs a supervisor signature before submitting.
      </Alert>
      <Alert variant='danger' title='Upload failed'>
        Two attachments could not be sent. Retry when connected.
      </Alert>
      <Alert variant='neutral' title='Draft saved'>
        This form is not published yet — only you can see it.
      </Alert>
      <Alert variant='info' icon='sparkles' title='Custom icon'>
        `icon` replaces the variant default while keeping the tint.
      </Alert>
      <Alert variant='neutral'>Body-only alert — `title` is optional.</Alert>
    </div>

    {/* ---- Table stack ---- */}
    <h3 className='theme-page__block-title'>
      DataTable · TableToolbar · ColumnsMenu · TableFooter
      <span className='theme-page__note'>
        Columns hug their content and the panel scrolls — they are never compressed.
      </span>
    </h3>
    <DataTableDemo />

    {/* ---- Card grid stack ---- */}
    <h3 className='theme-page__block-title'>
      DataCards · CardsLoadMore
      <span className='theme-page__note'>
        The shell, grid and states are the component&apos;s; card innards and pagination state are
        the module&apos;s.
      </span>
    </h3>
    <DataCardsDemo />

    {/* ---- Kanban stack ---- */}
    <h3 className='theme-page__block-title'>
      DataBoard
      <span className='theme-page__note'>
        Display-only Kanban — columns from `columns`, grouping via `columnKey`; card innards are
        module-specific via `renderCard`.
      </span>
    </h3>
    <DataBoardDemo />
  </section>
);

export default DataDisplaySection;
